"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSetupColumnSizes = useSetupColumnSizes;
const utils_1 = require("@react-aria/utils");
const react_1 = require("react");
const columns_1 = require("../utils/columns");
const use_debounce_1 = require("use-debounce");
/**
 * Calculates an array of sizes for each of our columns.
 *
 * We originally supported CSS grid-template-column definitions which allowed fancier,
 * dynamic/content-based widths, but have eventually dropped it mainly due to:
 *
 * 1. In virtual tables, a) the table never has all of the rows in DOM at a single time,
 * so any "content-based" widths will change as you scroll the table, which is weird, and
 * b) a sticky header and rows are put in different DOM parent elements by react-virtuoso,
 * so wouldn't arrive at the same "content-based" widths.
 *
 * 2. Using CSS grid but still have a row-level div for hover/focus targeting required
 * a "fake" `display: contents` div that couldn't have actually any styles applied to it.
 *
 * So we've just got with essentially fixed/deterministic widths, i.e. `px` or `percent` or
 * `fr`.
 *
 * Disclaimer that we roll our own `fr` b/c we're not in CSS grid anymore.
 */
function useSetupColumnSizes(style, columns, resizeRef, expandedColumnIds) {
    // Calculate the column sizes immediately rather than via the `debounce` method.
    // We do this for Storybook integrations that may use MockDate. MockDate changes the behavior of `new Date()`,
    // which is used internally by `useDebounce`, so the frozen clock means the callback is never called.
    const calculateImmediately = (0, react_1.useRef)(true);
    const [tableWidth, setTableWidth] = (0, react_1.useState)();
    // Calc our initial/first render sizes where we won't have a width yet
    const [columnSizes, setColumnSizes] = (0, react_1.useState)((0, columns_1.calcColumnSizes)(columns, tableWidth, style.minWidthPx, expandedColumnIds));
    const setTableAndColumnWidths = (0, react_1.useCallback)((width) => {
        setTableWidth(width);
        setColumnSizes((0, columns_1.calcColumnSizes)(columns, width, style.minWidthPx, expandedColumnIds));
    }, [setTableWidth, setColumnSizes, columns, style, expandedColumnIds]);
    // Used to recalculate our columns sizes when columns change
    (0, react_1.useEffect)(() => {
        var _a;
        if (!calculateImmediately.current) {
            const width = (_a = resizeRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth;
            width && setTableAndColumnWidths(width);
        }
    }, 
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-react-projects
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, setTableAndColumnWidths]);
    const setTableAndColumnWidthsDebounced = (0, use_debounce_1.useDebouncedCallback)(setTableAndColumnWidths, 100);
    const onResize = (0, react_1.useCallback)(() => {
        const target = resizeRef.current;
        if (target && target.clientWidth !== tableWidth) {
            if (calculateImmediately.current) {
                calculateImmediately.current = false;
                setTableAndColumnWidths(target.clientWidth);
            }
            else {
                setTableAndColumnWidthsDebounced(target.clientWidth);
            }
        }
    }, 
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-react-projects
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableWidth, setTableAndColumnWidths, setTableAndColumnWidthsDebounced]);
    (0, utils_1.useResizeObserver)({ ref: resizeRef, onResize });
    return columnSizes;
}
